"use client";

import React, { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import CouponSection from "@components/order/new/components/CouponSection";
import { useCoupon, useOrder } from "src/contexts";
import { useOrderQuery } from "src/hooks/useOrder";
import { orderQueryKey } from "src/hooks/useOrder";
import { orderApi } from "src/services/api";
import type {
  GetCouponDto,
  UpdateOrderCouponDto,
} from "src/services/generated-api";
import { assertCouponUsable, couponLookupError } from "src/utils/validate-coupon";
import { applyCouponToOrder } from "src/utils/apply-coupon";
import { goodsCents } from "src/utils/credit-amount";
import { rememberBundleExpired } from "src/hooks/useBundles";
import { getCurrencyBasedOnDomain } from "src/utils/domain.util";

/**
 * The coupon field on the canvas page, above "Add to cart".
 *
 * Same three requests as the cart drawer (check-coupon, add-coupon,
 * calculate-amount + re-read), with one difference that matters: the canvas
 * being designed is NOT in the cart yet. A coupon's minimum is therefore
 * checked against the cart's goods PLUS what the button is about to add —
 * otherwise a "10 € off 50 €" code typed beside a 59 € canvas would be
 * refused for an empty basket, and the customer would read "minimum 50 €"
 * under a price that meets it.
 *
 * The code is attached to the order straight away (the order exists from
 * the upload on; add-coupon does not price anything), so the server prices
 * the discount the moment the canvas is added, and the drawer opens on a
 * discounted total with the coupon shown as applied.
 */
export default function CanvasCoupon({
  pending,
}: {
  /** What "Add to cart" will add: price × quantity, in the display currency. */
  pending: { amount: number; currency: string };
}) {
  const { mainOrderId, guestToken, ensureOrderExists } = useOrder();
  const { data: order } = useOrderQuery(mainOrderId, guestToken);
  const { coupon, setCoupon } = useCoupon();
  const [couponValid, setCouponValid] = useState(false);
  const queryClient = useQueryClient();

  // A coupon already on the order (a reload, or one applied in the drawer)
  // shows as applied here too, exactly as the delivery step does. "Applied"
  // is read off the shared context, not synced into local state.
  useEffect(() => {
    if (order?.couponUsed) {
      setCoupon(order.couponUsed as GetCouponDto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.couponUsed]);
  const couponApplied = couponValid || !!coupon;

  const validateCoupon = useCallback(
    async (code: string): Promise<GetCouponDto> => {
      let couponData: GetCouponDto;
      try {
        const response = await orderApi.orderControllerCheckCoupon(
          code,
          guestToken ?? undefined,
        );
        couponData = response.data as GetCouponDto;
      } catch (error: unknown) {
        throw couponLookupError(error);
      }

      // The minimum is met by the basket as it will be once this canvas is
      // in it. Both figures are minor units of the market currency; the
      // display price falls back to euros only when the market has no
      // entry, which is also when the cart itself is priced in euros.
      const shopCurrency = getCurrencyBasedOnDomain();
      const pendingCents =
        pending.currency.split("-")[0] === shopCurrency.split("-")[0]
          ? pending.amount
          : 0;
      assertCouponUsable(couponData, {
        orderAmount: goodsCents(order) + pendingCents,
        currency: shopCurrency,
      });
      return couponData;
    },
    [guestToken, order, pending.amount, pending.currency],
  );

  const applyCoupon = useCallback(
    async (validated: GetCouponDto) => {
      // The upload already created the order; this only covers a restored
      // draft whose order has since gone.
      const { orderId, guestToken: token } = await ensureOrderExists();
      const updateData: UpdateOrderCouponDto = { couponCode: validated.code };
      const queryKey = orderQueryKey(orderId);
      return applyCouponToOrder({
        add: () =>
          orderApi.orderControllerUpdateOrderCoupon(orderId, updateData, token),
        markApplied: () => {
          setCoupon(validated);
          setCouponValid(true);
        },
        refresh: async () => {
          const recalculated = await orderApi.orderControllerCalculateAmount(
            orderId,
            token,
          );
          rememberBundleExpired(queryClient, orderId, recalculated.data);
          const updated = await orderApi.getOneBaseOrderControllerOrder(orderId);
          queryClient.setQueryData(queryKey, updated.data);
        },
        refetch: () =>
          queryClient.invalidateQueries({ queryKey }, { throwOnError: true }),
      });
    },
    [ensureOrderExists, queryClient, setCoupon],
  );

  const removeCoupon = useCallback(async () => {
    if (!mainOrderId) return;
    const token = guestToken ?? undefined;
    await orderApi.orderControllerRemoveCouponFromOrder(mainOrderId, token);
    setCoupon(null);
    setCouponValid(false);
    const recalculated = await orderApi.orderControllerCalculateAmount(
      mainOrderId,
      token,
    );
    rememberBundleExpired(queryClient, mainOrderId, recalculated.data);
    const updated = await orderApi.getOneBaseOrderControllerOrder(mainOrderId);
    queryClient.setQueryData(orderQueryKey(mainOrderId), updated.data);
  }, [guestToken, mainOrderId, queryClient, setCoupon]);

  return (
    <CouponSection
      embedded
      coupon={coupon}
      couponValid={couponApplied}
      setCouponValid={setCouponValid}
      validateCoupon={validateCoupon}
      applyCoupon={applyCoupon}
      removeCoupon={removeCoupon}
      creditMayApply
    />
  );
}
